import type { Imovel } from "@/types/imovel";

/**
 * Generates SEO-friendly property slug from property data  
 * Format: {public_id}-{tipo}-{quartos}-quartos-{bairro}-{cidade}-{estado}
 * Example: 02-casa-3-quartos-antonio-basilio-natal-rn
 */
export function generatePropertySlug(imovel: Imovel & { public_id?: number }): string {
  const parts: string[] = [];
  
  // Add public_id as first part (zero-padded to 2 digits)
  if (imovel.public_id) {
    parts.push(String(imovel.public_id).padStart(2, '0'));
  } else if (imovel.id) {
    // Fallback for properties without public_id: use UUID
    parts.push(imovel.id);
  } else {
    // Ultimate fallback (shouldn't happen)
    parts.push('00');
  }
  
  // Add property type
  const tipoMap: Record<string, string> = {
    'apartamento': 'apartamento',
    'casa': 'casa',
    'kitnet': 'kitnet',
    'studio': 'studio',
    'cobertura': 'cobertura',
    'sala-comercial': 'sala-comercial',
    'terreno': 'terreno',
    'chacara': 'chacara',
    'sitio': 'sitio'
  };
  
  const tipo = tipoMap[imovel.tipo] || imovel.tipo;
  parts.push(tipo);
  
  // Add bedrooms if available
  if (imovel.quartos && imovel.quartos > 0) {
    parts.push(`${imovel.quartos}-quartos`);
  }
  
  // Add neighborhood
  if (imovel.bairro) {
    parts.push(slugify(imovel.bairro));
  }
  
  // Add city
  if (imovel.cidade) {
    parts.push(slugify(imovel.cidade));
  }
  
  // Add state
  if (imovel.estado) {
    parts.push(slugify(imovel.estado).toLowerCase());
  }
  
  return parts.join('-');
}

/**
 * Creates property URL with tenant slug and SEO-friendly format
 * Format: /{tenantSlug}/imovel/{slug} or /imovel/{slug}
 */
export function createPropertyUrl(imovel: Imovel & { public_id?: number }, tenantSlug?: string): string {
  const slug = generatePropertySlug(imovel);
  const propertyPath = `/imovel/${slug}`;
  
  if (tenantSlug) {
    return `/${tenantSlug}${propertyPath}`;
  }
  
  return propertyPath;
}

/**
 * Extracts public_id from a SEO-friendly URL
 * Supports formats: /imovel/{public_id}-{slug}
 * Example: "02-casa-3-quartos-antonio-basilio-natal-rn" -> { publicId: 2, isLegacyUuid: false }
 */
export function extractPublicIdFromUrl(urlParam: string): { publicId: number | null; isLegacyUuid: boolean } {
  // 1. Check if the entire parameter is just a number
  if (/^\d+$/.test(urlParam)) {
    const publicId = parseInt(urlParam, 10);
    if (!isNaN(publicId) && publicId > 0) {
      return { publicId, isLegacyUuid: false };
    }
  }

  // 2. Handle new format: {public_id}-{slug}
  if (urlParam.includes('-')) {
    const parts = urlParam.split('-');
    const firstPart = parts[0];
    
    if (/^\d+$/.test(firstPart)) {
      const publicId = parseInt(firstPart, 10);
      if (!isNaN(publicId) && publicId > 0) {
        return { publicId, isLegacyUuid: false };
      }
    }
  }
  
  // 3. Check if it's a UUID or {uuid}-{slug}
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  
  if (uuidRegex.test(urlParam)) {
    return { publicId: null, isLegacyUuid: true };
  }

  if (urlParam.includes('-')) {
    const parts = urlParam.split('-');
    if (parts.length >= 5) {
      const possibleUuid = parts.slice(0, 5).join('-');
      if (uuidRegex.test(possibleUuid)) {
        return { publicId: null, isLegacyUuid: true };
      }
    }
  }
  
  // If no valid format found
  return { publicId: null, isLegacyUuid: false };
}

/**
 * Legacy function for UUID extraction (for redirects)
 */
export function extractUuidFromUrl(urlParam: string): string | null {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  
  // First check if it's a valid UUID format
  if (uuidRegex.test(urlParam)) {
    return urlParam;
  }
  
  // Handle format: {uuid}-{slug}
  if (urlParam.includes('-')) {
    const parts = urlParam.split('-');
    if (parts.length >= 5) {
      const possibleUuid = parts.slice(0, 5).join('-');
      if (uuidRegex.test(possibleUuid)) {
        return possibleUuid;
      }
    }
  }
  
  return null;
}

/**
 * Generates SEO title for property pages
 */
export function generatePropertySeoTitle(imovel: Imovel & { public_id?: number }): string {
  const tipo = imovel.tipo === 'apartamento' ? 'Apartamento' : 
               imovel.tipo === 'casa' ? 'Casa' :
               imovel.tipo === 'cobertura' ? 'Cobertura' :
               imovel.tipo === 'kitnet' ? 'Kitnet' :
               imovel.tipo === 'studio' ? 'Studio' :
               imovel.tipo === 'sala-comercial' ? 'Sala Comercial' :
               imovel.tipo === 'terreno' ? 'Terreno' :
               imovel.tipo === 'chacara' ? 'Chácara' :
               imovel.tipo === 'sitio' ? 'Sítio' :
               'Imóvel';
  
  const finalidade = imovel.finalidade === 'venda' ? 'à venda' : 'para aluguel';
  const quartos = imovel.quartos && imovel.quartos > 0 ? ` ${imovel.quartos} quartos` : '';
  const location = `${imovel.bairro}, ${imovel.cidade}/${imovel.estado}`;
  
  return `${tipo}${quartos} ${finalidade} – ${location}`;
}

/**
 * Generates meta description for property pages
 */
export function generatePropertyMetaDescription(imovel: Imovel & { public_id?: number }): string {
  const tipo = imovel.tipo === 'apartamento' ? 'apartamento' : 
               imovel.tipo === 'casa' ? 'casa' :
               imovel.tipo === 'cobertura' ? 'cobertura' :
               imovel.tipo;
  
  const finalidade = imovel.finalidade === 'venda' ? 'à venda' : 'para aluguel';
  const location = `${imovel.bairro}, ${imovel.cidade}`;
  
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };
  
  const price = formatPrice(Number(imovel.preco));
  const area = imovel.area_construida ? ` com ${imovel.area_construida}m²` : '';
  const quartos = imovel.quartos && imovel.quartos > 0 ? ` e ${imovel.quartos} quartos` : '';
  
  return `${tipo.charAt(0).toUpperCase() + tipo.slice(1)} ${finalidade} em ${location} por ${price}${area}${quartos}. Confira fotos, detalhes e agende uma visita.`;
}

/**
 * Helper function to create URL-friendly slugs
 */
function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove accents
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single
    .trim();
}